import api from '@/lib/api';

export interface ExternalConversation {
  id: string;
  ticket_id: string;
  title: string;
  description?: string;
  type: 'supplier' | 'warehouse' | 'shipping' | 'vendor' | 'partner' | 'other';
  status: 'active' | 'resolved' | 'archived';
  external_party_name: string;
  external_party_email: string;
  external_party_company?: string;
  created_by: string;
  assigned_agent_id?: string;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  created_by_email?: string;
}

export interface ExternalConversationMessage {
  id: string;
  external_conversation_id: string;
  content: string;
  direction: 'outgoing' | 'incoming';
  sender_name: string;
  sender_email: string;
  agent_id?: string;
  sent_at: string;
  created_at: string;
  updated_at: string;
}

export interface CreateExternalConversationRequest {
  ticket_id: string;
  title: string;
  description?: string;
  type: 'supplier' | 'warehouse' | 'shipping' | 'vendor' | 'partner' | 'other';
  external_party_name: string;
  external_party_email: string;
  external_party_company?: string;
  assigned_agent_id?: string;
}

export interface SendMessageRequest {
  content: string;
}

export interface ExternalConversationsResponse {
  success: boolean;
  data: {
    ticketId: string;
    conversations: ExternalConversation[];
  };
}

export interface ExternalConversationResponse {
  success: boolean;
  data: {
    conversation: ExternalConversation;
  };
}

export interface ExternalConversationMessagesResponse {
  success: boolean;
  data: {
    conversationId: string;
    messages: ExternalConversationMessage[];
  };
}

export interface SendMessageResponse {
  success: boolean;
  message: string;
  data: {
    emailSent: boolean;
    messageRecord: ExternalConversationMessage;
  };
}

const externalConversationsService = {
  // Get external conversations for a ticket
  async getByTicketId(ticketId: string): Promise<ExternalConversation[]> {
    const response = await api.get<ExternalConversationsResponse>(`/external-conversations/ticket/${ticketId}`);
    return response.data.data.conversations;
  },

  // Get single external conversation
  async getById(conversationId: string): Promise<ExternalConversation> {
    const response = await api.get<ExternalConversationResponse>(`/external-conversations/${conversationId}`);
    return response.data.data.conversation;
  },

  // Create new external conversation
  async create(conversationData: CreateExternalConversationRequest): Promise<ExternalConversation> {
    const response = await api.post<ExternalConversationResponse>('/external-conversations', conversationData);
    return response.data.data.conversation;
  },

  // Update external conversation
  async update(conversationId: string, updateData: Partial<CreateExternalConversationRequest>): Promise<ExternalConversation> {
    const response = await api.put<ExternalConversationResponse>(`/external-conversations/${conversationId}`, updateData);
    return response.data.data.conversation;
  },

  // Delete external conversation
  async delete(conversationId: string): Promise<void> {
    await api.delete(`/external-conversations/${conversationId}`);
  },

  // Get messages for external conversation
  async getMessages(conversationId: string): Promise<ExternalConversationMessage[]> {
    const response = await api.get<ExternalConversationMessagesResponse>(`/external-conversations/${conversationId}/messages`);
    return response.data.data.messages;
  },

  // Send message in external conversation
  async sendMessage(conversationId: string, messageData: SendMessageRequest): Promise<ExternalConversationMessage> {
    const response = await api.post<SendMessageResponse>(`/external-conversations/${conversationId}/messages`, messageData);
    return response.data.data.messageRecord;
  },

  // Get conversation stats
  async getStats(ticketId?: string) {
    const url = ticketId ? `/external-conversations/stats?ticket_id=${ticketId}` : '/external-conversations/stats';
    const response = await api.get(url);
    return response.data.data;
  }
};

export default externalConversationsService;